package com.essntl.features.proposal.ui.addeditproposal

import androidx.compose.ui.text.AnnotatedString
import com.essntl.core.stripe.domain.StripeClient
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.utils.update
import com.essntl.core.utils.viewmodel.*
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.client.domain.repository.ClientRepository
import com.essntl.features.file.domain.model.FileModel
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.file.ui.FileViewModel
import com.essntl.features.paymentlink.domain.repository.PaymentLinkRepository
import com.essntl.features.price.domain.model.proposalserviceprice.ProposalServicePriceModel
import com.essntl.features.price.domain.repository.PriceRepository
import com.essntl.features.profile.domain.repository.ProfileRepository
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.model.ProposalServiceModel
import com.essntl.features.proposal.domain.model.shareLink
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.features.proposal.domain.use_case.UpdateProposalServiceUseCase
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.domain.repository.TaskRepository
import com.essntl.features.task.domain.use_case.AddTaskUseCase
import com.essntl.features.task.domain.use_case.DeleteTaskUseCase
import com.essntl.features.task.domain.use_case.UpdateTaskUseCase
import com.essntl.features.travelagent.domain.repository.TravelAgentRepository
import com.essntl.toastmanager.ToastManager
import com.mohamedrejeb.calf.core.PlatformContext
import com.mohamedrejeb.richeditor.model.RichTextState
import core.config.FeatureConfig
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

class AddEditProposalViewModel(
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val proposalRepository: ProposalRepository,
    private val priceRepository: PriceRepository,
    private val travelAgentRepository: TravelAgentRepository,
    private val profileRepository: ProfileRepository,
    private val taskRepository: TaskRepository,
    private val areaRepository: AreaRepository,
    private val categoryRepository: CategoryRepository,
    private val paymentLinkRepository: PaymentLinkRepository,
    private val clientRepository: ClientRepository,
    private val stripeClient: StripeClient,
    private val updateProposalServiceUseCase: UpdateProposalServiceUseCase,
    private val state: MutableStateFlow<AddEditProposalState>,
    private val toastManager: ToastManager,
    private val proposalId: String?,
    private val validation: AddEditProposalValidation,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val updateTaskUseCase: UpdateTaskUseCase,
    private val deleteTaskUseCase: DeleteTaskUseCase,
    private val addTaskUseCase: AddTaskUseCase,
    fileRepository: FileRepository,
) : ViewModel {

    private val fileViewModel = FileViewModel(
        fileRepository = fileRepository,
        scope = scope,
        loadingManager = loadingManager,
    )

    init {
        saveOpenedProposal()

        collectFileState()

        getAreas()
        getAllCategory()
        getCurrentProfile()
        getAllProfile()

        if (!proposalId.isNullOrEmpty()) {
            getProposal(proposalId)
            getAllProposalService(proposalId)
            getProposalPaymentLink(proposalId)
            getAllProposalTask(proposalId)
        }

        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getCurrentProfile },
            setState = { newState -> state.update { it.copy(getCurrentProfile = newState) } },
        )

        // Add task Use Case
        collectSuccessEvent(
            flow = addTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            stateListGetSet = arrayOf(
                StateListGetSet(
                    getState = { state.value.getAllProposalTask },
                    setState = { s -> state.update { it.copy(getAllProposalTask = s) } },
                    // Add only itinerary tasks
                    predicate = { !it.itinerary?.id.isNullOrEmpty() },
                ),
                StateListGetSet(
                    getState = { state.value.getAllProposalTask },
                    setState = { s -> state.update { it.copy(getAllProposalTask = s) } },
                    // Add only service tasks
                    predicate = { it.itinerary?.id.isNullOrEmpty() },
                ),
            ),
        )

        // Update task Use Case
        collectLoading(
            flow = updateTaskUseCase.state.map { it.loadingSet },
            setState = { state.update { s -> s.copy(updateTaskLoading = it) } },
        )
        collectSuccessEvent(
            flow = updateTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            update = { old, new -> new.copy(proposal = old.proposal) },
            stateListGetSet = arrayOf(
                StateListGetSet(
                    getState = { state.value.getAllProposalTask },
                    setState = { s -> state.update { it.copy(getAllProposalTask = s) } },
                ),
                StateListGetSet(
                    getState = { state.value.getAllProposalTask },
                    setState = { s -> state.update { it.copy(getAllProposalTask = s) } },
                ),
            ),
        )

        // Delete task Use Case
        collectLoading(
            flow = deleteTaskUseCase.state.map { it.loadingSet },
            setState = { state.update { s -> s.copy(deleteTaskLoading = it) } },
        )
        collectSuccessEvent(
            flow = deleteTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            stateListGetSet = arrayOf(
                StateListGetSet(
                    getState = { state.value.getAllProposalTask },
                    setState = { s -> state.update { it.copy(getAllProposalTask = s) } },
                ),
                StateListGetSet(
                    getState = { state.value.getAllProposalTask },
                    setState = { s -> state.update { it.copy(getAllProposalTask = s) } },
                ),
            ),
        )
    }

    private fun saveOpenedProposal() {
        if (!proposalId.isNullOrEmpty())
            proposalRepository.saveOpenedProposal(proposalId)
    }

    private var getSharedCoverIMageStateJob: Job? = null
    private fun collectFileState() {
        // collecting coverImage
        getSharedCoverIMageStateJob?.cancel()
        getSharedCoverIMageStateJob = scope.launch {
            fileViewModel.state.collect { data ->
                state.update {
                    it.copy(
                        fileState = data,
                    )
                }
            }
        }
    }

    fun onEvent(event: AddEditProposalEvent) {
        when (event) {
            is AddEditProposalEvent.GetProposal -> getProposal()
            is AddEditProposalEvent.UpdateProposalServicePrices -> updateProposalServicePrices(event.prices)
            is AddEditProposalEvent.UpdateProposalDates -> updateProposalDates(event)
            is AddEditProposalEvent.AddProposal -> addProposal(event.context)
            is AddEditProposalEvent.EditProposal -> editProposal(event.context)
            is AddEditProposalEvent.PublishProposal -> publishProposal(event)
            is AddEditProposalEvent.ClearPublish -> clearPublish()
            is AddEditProposalEvent.AddProposalService -> addProposalService(event)
            is AddEditProposalEvent.DeleteProposalService -> deleteProposalService(event)
            is AddEditProposalEvent.MarkProposalServiceAsPaid -> markProposalServiceAsPaid(event)
            is AddEditProposalEvent.ClearSuccessState -> clearSuccessState()
            is AddEditProposalEvent.UpdateProposalFields -> updateProposalFields(event)
            is AddEditProposalEvent.UpdateProposalTravelAgent -> updateProposalTravelAgent(event)
            is AddEditProposalEvent.SelectProposalServiceDate -> selectProposalServiceDate(event)
            is AddEditProposalEvent.AddProposalServiceToGroup -> addProposalServiceToGroup(event)
            is AddEditProposalEvent.ProposalServiceGrouped -> proposalServiceGrouped(event)
            is AddEditProposalEvent.UpdateSelectedProposalService -> updateSelectedProposalService(event)
            is AddEditProposalEvent.UnselectProposalService -> unselectProposalService(event)
            is AddEditProposalEvent.UpdateProposalService -> updateProposalService(event)
            is AddEditProposalEvent.AddClient -> addClient(event)
            is AddEditProposalEvent.EditClient -> editClient(event)
            is AddEditProposalEvent.DeleteClient -> deleteClient(event)
            is AddEditProposalEvent.UpdateAlertSaveProposalDialog -> updateAlertSaveProposalDialog(event)
            is AddEditProposalEvent.AddCoverImage -> addCoverImage(event)
            is AddEditProposalEvent.DeleteCoverImage -> deleteCoverImage()
            is AddEditProposalEvent.EditTaskStatus -> editTaskStatus(event)
            is AddEditProposalEvent.DeleteTask -> deleteTask(event)
            is AddEditProposalEvent.HideServiceOnProposal -> hideServiceOnProposal(event)

        }
    }

    private fun editTaskStatus(event: AddEditProposalEvent.EditTaskStatus) =
        scope.launch {
            updateTaskUseCase.update(
                task = TaskModel.empty.copy(
                    id = event.taskId,
                    status = event.status,
                ),
            )
        }

    private fun deleteTask(event: AddEditProposalEvent.DeleteTask) =
        scope.launch {
            deleteTaskUseCase.delete(
                task = event.task,
            )
        }

    private fun getAllProposalTask(proposalId: String) =
        getData(
            getData = { taskRepository.getAllByProposalId(proposalId) },
            getState = { state.value.getAllProposalTask },
            setState = { newState -> state.update { it.copy(getAllProposalTask = newState) } },
        )

    private fun getProposal() {
        val id = state.value.getProposal.data?.id
        if (id.isNullOrEmpty()) return
        getProposal(id)
        getAllProposalService(id)
        getProposalPaymentLink(id)
        getAllProposalTask(id)

        // refresh dynamic prices
        if (FeatureConfig.hasDynamicPricing)
            calculateServicePrice(
                proposalServiceIds = state.value.proposalServiceList
                    //TODO: put back when shipping
//                    .filter { it.dynamicPriceEnabled == true }
                    .filter { it.service.hasDynamicPrice == true }
                    .map { it.id },
                update = true,
            )
    }

    private fun updateProposalDates(event: AddEditProposalEvent.UpdateProposalDates) =
        getData(
            getData = {
                proposalRepository.updateProposalDates(
                    event.proposalId,
                    event.startDate,
                )
            },
            getState = { state.value.updateProposalDates },
            setState = { newState -> state.update { it.copy(updateProposalDates = newState) } },
            onSuccess = {
                editProposal(
                    context = event.context,
                    refresh = true,
                )
            },
        )

    private fun updateProposalServicePrices(prices: List<ProposalServicePriceModel>) {
        val currentPrices = state.value.getAllProposalServicesPrices.data.orEmpty()
        state.update {
            it.copy(
                getAllProposalServicesPrices = it.getAllProposalServicesPrices.copy(
                    data = currentPrices
                        .map { p ->
                            prices.find { price -> price.id == p.id } ?: p
                        },
                ),
            )
        }
    }

    private fun addProposalServicePrices(prices: List<ProposalServicePriceModel>) {
        val currentPrices = state.value.getAllProposalServicesPrices.data.orEmpty()
        state.update {
            it.copy(
                getAllProposalServicesPrices = it.getAllProposalServicesPrices.copy(
                    data = currentPrices + prices,
                ),
            )
        }
    }

    private fun addCoverImage(event: AddEditProposalEvent.AddCoverImage) {
        state.value.proposal.value = state.value.proposal.value.copy(
            coverImage = event.file,
        )
        fileViewModel.addFiles(listOf(event.file))
    }

    private fun deleteCoverImage() {
        state.value.proposal.value = state.value.proposal.value.copy(
            coverImage = FileModel.empty,
        )
        fileViewModel.deleteFile(0)
    }

    fun isProposalChanged(): Boolean {
        // Proposal service list
        val oldProposalServiceList = state.value.getAllProposalService.data
            .orEmpty()
            .map {
                it.copy(editedDescription = RichTextState().setHtml(it.editedDescription ?: "").toHtml())
            }
        val newProposalServiceList = state.value.proposalServiceList
            .map {
                it.copy(editedDescription = RichTextState().setHtml(it.editedDescription ?: "").toHtml())
            }
        if (oldProposalServiceList != newProposalServiceList) return true

        // Proposal fields
        val oldProposal = state.value.getProposal.data ?: ProposalModel.empty
        val newProposal = state.value.proposal.value
        val changedFields = oldProposal.getChangedFields(newProposal)

        return !changedFields.isEmpty
    }

    private fun getAllCategory() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { newState -> state.update { it.copy(getAllCategory = newState) } },
        )

    private fun getCurrentProfile() =
        scope.launch {
            getCurrentUserUseCase.get()
        }

    private fun getAllProfile() =
        getData(
            getData = { profileRepository.getAll() },
            getState = { state.value.getAllProfile },
            setState = { newState -> state.update { it.copy(getAllProfile = newState) } },
        )

    private fun getAllClient(ids: Set<String>) =
        getData(
            getData = { clientRepository.getByIds(ids.toList()) },
            getState = { state.value.getAllClient },
            setState = { newState -> state.update { state -> state.copy(getAllClient = newState) } },
        )

    private fun getAreas() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { newState -> state.update { it.copy(getAllArea = newState) } },
        )

    private fun getProposal(proposalId: String) {

        fileViewModel.setState(State())

        getData(
            getData = { proposalRepository.getProposal(proposalId) },
            getState = { state.value.getProposal },
            setState = { newState -> state.update { it.copy(getProposal = newState) } },
            onSuccess = { data ->
                state.value.proposal.value = data

                val coverImage = data.coverImage
                if (coverImage != null)
                    fileViewModel.getFiles(listOf(coverImage.id))
                else
                    fileViewModel.setState(State.success(emptyList()))

                if (!data.clients.isNullOrEmpty()) {
                    getAllClient(data.clients.orEmpty().toSet())
                }

                data.shareCode?.let {
                    getLatestProposalHistory(it)
                }
            },
        )
    }

    private fun getLatestProposalHistory(shareCode: String) =
        getData(
            getData = { proposalRepository.getLatestProposalHistory(shareCode) },
            getState = { state.value.getProposalHistory },
            setState = { data -> state.update { it.copy(getProposalHistory = data) } },
            onSuccess = { p ->
                p?.updatedBy?.let {
                    getProfile(it)
                }

                p?.id?.let {
                    getAllProposalHistoryService(it)
                }
            },
        )

    private fun getProfile(id: String) =
        getData(
            getData = { profileRepository.getById(id) },
            getState = { state.value.getProfile },
            setState = { data -> state.update { it.copy(getProfile = data) } },
        )

    private fun getProposalPaymentLink(proposalId: String) =
        getData(
            getData = { paymentLinkRepository.getAllByProposalId(proposalId) },
            getState = { state.value.getPaymentLinks },
            setState = { newState -> state.update { it.copy(getPaymentLinks = newState) } },
            onSuccess = { paymentLinks ->
                state.value.paymentLinks.value = paymentLinks
            },
        )

    private fun getAllProposalService(proposalId: String) =
        getData(
            getData = { proposalRepository.getAllProposalService(proposalId) },
            getState = { state.value.getAllProposalService },
            setState = { newState -> state.update { it.copy(getAllProposalService = newState) } },
            onSuccess = { proposalServiceList ->
                state.update {
                    it.copy(
                        proposalServiceList = proposalServiceList,
                    )
                }

                if (FeatureConfig.hasDynamicPricing)
                    getAllProposalServicesPrices(
                        proposalServiceIds = proposalServiceList.map { it.id },
                    )
            },
        )

    private fun getAllProposalServicesPrices(proposalServiceIds: List<String>) =
        getData(
            getData = { priceRepository.getPricesByProposalServices(proposalServiceIds) },
            getState = { state.value.getAllProposalServicesPrices },
            setState = { newState -> state.update { it.copy(getAllProposalServicesPrices = newState) } },
        )

    private fun calculateServicePrice(
        proposalServiceIds: List<String>,
        update: Boolean = false,
    ) =
        scope.launch {
            proposalServiceIds
                .forEach { id ->
                    state.value = state.value.copy(
                        updateProposalServicesPrices = state.value.updateProposalServicesPrices + id,
                    )

                    proposalRepository
                        .calculateServicePrice(id)
                        .onSuccess {
                            priceRepository
                                .getPricesByProposalService(id)
                                .onSuccess { prices ->
                                    if (update)
                                        updateProposalServicePrices(prices) else
                                        addProposalServicePrices(prices)
                                    state.update {
                                        it.copy(
                                            updateProposalServicesPrices = it.updateProposalServicesPrices - id,
                                        )
                                    }
                                }
                                .onFailure {
                                    state.update {
                                        it.copy(
                                            updateProposalServicesPrices = it.updateProposalServicesPrices - id,
                                        )
                                    }
                                }
                        }.onFailure {
                            state.update {
                                it.copy(
                                    updateProposalServicesPrices = it.updateProposalServicesPrices - id,
                                )
                            }
                        }
                }
        }

    private fun getAllProposalHistoryService(proposalId: String) =
        getData(
            getData = { proposalRepository.getAllProposalService(proposalId) },
            getState = { state.value.getAllProposalHistoryService },
            setState = { newState -> state.update { it.copy(getAllProposalHistoryService = newState) } },
        )

    private var addProposalJob: Job? = null
    private fun addProposal(context: PlatformContext) {
        if (addProposalJob?.isActive == true) return
        addProposalJob = scope.launch {
            if (!insertFiles(context)) return@launch

            getData(
                getData = {
                    val newProposal =
                        state.value.proposal.value.copy(
                            status =
                                if (state.value.filteredProposalServices.isNotEmpty()) {
                                    ProposalModel.Status.InProgress
                                } else {
                                    state.value.proposal.value.status
                                },
                        )

                    proposalRepository.addProposal(newProposal)
                },
                getState = { state.value.addEditProposal },
                setState = { newState -> state.update { it.copy(addEditProposal = newState) } },
                onSuccess = { proposal ->
                    val proposalServiceList =
                        state.value.proposalServiceList
                            .map { ps ->
                                ps.copy(
                                    proposalId = proposal.id,

                                    // TODO Pricing: uncomment to ship
//                                    dynamicPriceEnabled = ps.service.hasDynamicPrice,
                                )
                            }

                    if (proposalServiceList.isNotEmpty())
                        proposalRepository
                            .addAllProposalService(proposalServiceList)
                            .onSuccess { newProposalServiceList ->
                                state.update {
                                    it.copy(
                                        proposalServiceList = newProposalServiceList,
                                        getAllProposalService = State.success(newProposalServiceList),
                                    )
                                }

                                // Calculate Prices
                                // TODO Pricing: uncomment to ship
//                                val proposalServicesToCalculatePrices =
//                                    newProposalServiceList.filter { it.dynamicPriceEnabled == true }

                                val proposalServicesToCalculatePrices =
                                    newProposalServiceList.filter { it.service.hasDynamicPrice == true }

                                if (FeatureConfig.hasDynamicPricing) {
                                    if (proposalServicesToCalculatePrices.isNotEmpty())
                                        calculateServicePrice(proposalServicesToCalculatePrices.map { it.id })
                                }
                            }

                    state.update {
                        it.copy(
                            getProposal = State.success(proposal),
                        )
                    }.also {
                        state.value.proposal.value = proposal
                    }

                    toastManager.success(
                        title = "Saved",
                        description = "New proposal has been saved",
                    )
                },
            )
        }
    }

    private fun deactivatePaymentLink() {
        val activePaymentLinks = state.value.paymentLinks.value.filter { it.active == true && it.id.isNotEmpty() }

        scope.launch {
            activePaymentLinks.forEach { paymentLink ->
                paymentLinkRepository.update(
                    paymentLink = paymentLink.copy(active = false),
                )

                paymentLink.linkId?.let { linkId ->
                    stripeClient.deactivatePaymentLink(
                        paymentLinkId = linkId,
                        active = false,
                    )
                }
            }
        }
    }

    private var editProposalJob: Job? = null
    private fun editProposal(
        context: PlatformContext,
        refresh: Boolean = false,
    ) {
        if (editProposalJob?.isActive == true || state.value.addEditProposal.isLoading) return

        editProposalJob = scope.launch {
            state.update {
                it.copy(
                    addEditProposal = State.loading(),
                )
            }

            // If insert images fails, we don't want to continue
            if (!insertFiles(context)) return@launch

            deactivatePaymentLink()

            var deleteOperation = false
            var addOperation = false
            var updateOperation = false
            val isProposalServicesEmpty = state.value.getAllProposalService.data.orEmpty().isEmpty()

            // Proposal service operations
            val deleteProposalServiceDeferred = async {
                deleteOperation = editProposalDeleteProposalServiceList(
                    getAllProposalService = state.value.getAllProposalService.data.orEmpty(),
                    proposalServiceList = state.value.proposalServiceList,
                )
            }

            deleteProposalServiceDeferred.await()

            val addProposalServiceDeferred = async {
                addOperation = editProposalAddProposalServiceList(
                    proposalServiceList = state.value.proposalServiceList,
                )
            }

            addProposalServiceDeferred.await()

            val editProposalServiceDeferred = async {
                updateOperation = editProposalEditProposalServiceList(
                    getAllProposalService = state.value.getAllProposalService.data.orEmpty(),
                    proposalServiceList = state.value.proposalServiceList,
                )
            }

            editProposalServiceDeferred.await()

            state.update {
                it.copy(
                    getAllProposalService = State(
                        data = state.value.proposalServiceList,
                    ),
                )
            }

            val newProposal =
                state.value.getProposal.data?.getChangedFields(
                    state.value.proposal.value.copy(
                        status =
                            if (isProposalServicesEmpty && state.value.getAllProposalService.data.orEmpty()
                                    .isNotEmpty()
                            ) {
                                ProposalModel.Status.InProgress
                            } else {
                                state.value.proposal.value.status
                            },
                    ),
                )

            val travelAgent = state.value.proposal.value.travelAgent
            val owner = state.value.proposal.value.owner

            if (newProposal?.isEmpty == false) {
                proposalRepository
                    .updateProposal(
                        newProposal.copy(
                            travelAgent = travelAgent,
                            owner = owner,
                        ),
                    )
                    .onSuccess { proposal ->
                        if (state.value.proposal.value.status == ProposalModel.Status.Confirmed
                            || state.value.proposal.value.status == ProposalModel.Status.ItineraryReady
                        ) proposalRepository
                            .updateProposalToItinerary(proposal = proposal)
                            .onSuccess {
                                proposalRepository
                                    .getProposal(proposal.id)
                                    .onSuccess { p ->
                                        state.update {
                                            it.copy(
                                                getProposal = State.success(p),
                                                addEditProposal = State.success(p),
                                            )
                                        }

                                        state.value.proposal.value = p
                                    }
                            }
                            .onFailure {
                                state.update {
                                    it.copy(
                                        addEditProposal = State.failure(),
                                    )
                                }
                            }
                        else {
                            state.update {
                                it.copy(
                                    getProposal = State.success(proposal),
                                    addEditProposal = State.success(proposal),
                                )
                            }

                            state.value.proposal.value = proposal

                            if (refresh)
                                getProposal()
                        }
                    }
                    .onFailure {
                        state.update {
                            it.copy(
                                addEditProposal = State.failure(),
                            )
                        }
                    }
            }

            val isSuccess =
                deleteOperation || addOperation || updateOperation ||
                    (state.value.addEditProposal.isSuccess)

            if (isSuccess)
                toastManager.success(
                    title = "Updated",
                    description = "Proposal has been updated",
                )
        }

        editProposalJob?.invokeOnCompletion {
            state.update {
                it.copy(
                    addEditProposal = it.addEditProposal.copy(isLoading = false),
                )
            }
        }
    }

    private fun publishProposal(event: AddEditProposalEvent.PublishProposal) {

        state.update {
            it.copy(
                publish = State.loading(),
            )
        }

        scope.launch {
            val proposal = state.value.getProposal.data ?: return@launch

            proposalRepository.publishProposal(
                proposal.id,
                event.description,
            )
                .onSuccess {
                    // Copy proposal share link to clipboard
                    event.clipboardManager.setText(AnnotatedString(proposal.shareLink))

                    toastManager.success(
                        title = "Share",
                        description = "Proposal link copied to clipboard",
                    )

                    event.clipboardManager
                    // Update last proposal history data
                    proposal.shareCode?.let { s ->
                        getLatestProposalHistory(s)
                    }

                    state.update {
                        it.copy(
                            publish = State.success(Unit),
                        )
                    }.also {
                        proposalRepository
                            .getProposal(proposal.id)
                            .onSuccess { p ->
                                state.update {
                                    it.copy(
                                        getProposal = it.getProposal.copy(
                                            data = p,
                                        ),
                                    )
                                }.also {
                                    state.value.proposal.value = p
                                }
                            }
                    }
                }
                .onFailure {
                    state.update {
                        it.copy(
                            publish = State.failure(),
                        )
                    }
                }
        }
    }

    private fun clearPublish() =
        state.update {
            it.copy(
                publish = State(),
            )
        }

    private suspend fun editProposalDeleteProposalServiceList(
        getAllProposalService: List<ProposalServiceModel>,
        proposalServiceList: List<ProposalServiceModel>,
    ): Boolean {
        var isSuccess = false

        val proposalServiceToDelete =
            getAllProposalService
                .filter { proposalService ->
                    proposalService.id !in proposalServiceList.map { it.id }
                }

        if (proposalServiceToDelete.isNotEmpty()) {
            val updatedProposalServiceToDelete = proposalServiceToDelete.map {
                it.copy(
                    archived = true,
                    supplier = it.supplier,
                )
            }

            updatedProposalServiceToDelete.forEach { p ->
                updateProposalServiceUseCase.update(
                    proposalService = p,
                    ignoreLoading = true,
                    showSuccessToast = false,
                    onSuccess = {
                        isSuccess = true
                    },
                )
            }
        }

        return isSuccess
    }

    private suspend fun editProposalAddProposalServiceList(
        proposalServiceList: List<ProposalServiceModel>,
    ): Boolean {
        var isSuccess = false
        val itineraryId = state.value.getProposal.data?.itineraryId
        val toAddItineraryServiceList = mutableListOf<ProposalServiceModel>()

        val filteredProposalServiceList =
            proposalServiceList
                .filter {
                    it.proposalId.isNullOrEmpty()
                }
                .map {
                    it.copy(
                        proposalId = state.value.proposal.value.id,

                        // TODO Pricing: uncomment to ship
//                        dynamicPriceEnabled = it.service.hasDynamicPrice,
                    )
                }

        if (filteredProposalServiceList.isNotEmpty()) {
            proposalRepository
                .addAllProposalService(filteredProposalServiceList)
                .onSuccess { addedProposalServiceList ->
                    val updatedProposalServiceList =
                        filteredProposalServiceList.map { proposalServiceModel ->
                            val ps = addedProposalServiceList
                                .find {
                                    it.index == proposalServiceModel.index &&
                                        it.startDate == proposalServiceModel.startDate
                                }

                            ps ?: proposalServiceModel
                        }

                    // Calculate Prices
                    // TODO Pricing: uncomment to ship
//                    val proposalServicesToCalculatePrices =
//                        updatedProposalServiceList.filter { it.dynamicPriceEnabled == true }

                    val proposalServicesToCalculatePrices =
                        updatedProposalServiceList.filter { it.service.hasDynamicPrice == true }

                    if (FeatureConfig.hasDynamicPricing) {
                        if (proposalServicesToCalculatePrices.isNotEmpty())
                            calculateServicePrice(proposalServicesToCalculatePrices.map { it.id })
                    }

                    addedProposalServiceList.forEach { proposalService ->
                        if (proposalService.paid == true) {
                            toAddItineraryServiceList.add(
                                proposalService.copy(
                                    proposalId = null,
                                    itineraryId = itineraryId,
                                ),
                            )
                        }
                    }

                    state.update {
                        it.copy(
                            proposalServiceList = state.value.proposalServiceList.map { proposalServiceModel ->
                                val updatedProposalService =
                                    updatedProposalServiceList
                                        .find {
                                            it.id == proposalServiceModel.id ||
                                                (it.index == proposalServiceModel.index &&
                                                    it.startDate == proposalServiceModel.startDate)
                                        }

                                updatedProposalService ?: proposalServiceModel
                            },
                        )
                    }

                    isSuccess = true
                }
        }

        // Add itinerary service list
        if (toAddItineraryServiceList.isNotEmpty() && !itineraryId.isNullOrEmpty()) {
            proposalRepository
                .addAllItineraryService(toAddItineraryServiceList)
        }

        return isSuccess
    }

    private suspend fun editProposalEditProposalServiceList(
        getAllProposalService: List<ProposalServiceModel>,
        proposalServiceList: List<ProposalServiceModel>,
    ): Boolean {
        var isSuccess = false
        val itineraryId = state.value.getProposal.data?.itineraryId
        val toAddItineraryServiceList = mutableListOf<ProposalServiceModel>()

        getAllProposalService
            .forEach { oldProposalService ->
                val newProposalService =
                    proposalServiceList
                        .find { it.id == oldProposalService.id }
                        ?: return@forEach

                val changedFields = oldProposalService.getChangedFields(newProposalService)

                if (changedFields.paid == true) {
                    toAddItineraryServiceList.add(
                        newProposalService.copy(
                            proposalId = null,
                            itineraryId = itineraryId,
                        ),
                    )
                }

                updateProposalServiceUseCase.update(
                    proposalService = changedFields,
                    ignoreLoading = true,
                    showSuccessToast = false,
                    onSuccess = { ps ->


                        // Calculate Prices

                        // TODO: Pricing: uncomment to ship
//                        val proposalServicesToCalculatePrices =
//                            listOf(ps).filter { it.dynamicPriceEnabled == true }

                        val proposalServicesToCalculatePrices =
                            listOf(ps).filter { it.service.hasDynamicPrice == true }

                        if (FeatureConfig.hasDynamicPricing) {
                            if (proposalServicesToCalculatePrices.isNotEmpty())
                                calculateServicePrice(
                                    proposalServiceIds = proposalServicesToCalculatePrices.map { it.id },
                                    update = true,
                                )
                        }

                        isSuccess = true
                    },
                )
            }

        // Add itinerary service list
        if (toAddItineraryServiceList.isNotEmpty() && !itineraryId.isNullOrEmpty()) {
            proposalRepository
                .addAllItineraryService(toAddItineraryServiceList)
        }

        return isSuccess
    }

    @OptIn(ExperimentalUuidApi::class)
    private fun addProposalService(event: AddEditProposalEvent.AddProposalService) {
        if (state.value.addEditProposal.isLoading)
            return

        val proposalServiceList = state.value.proposalServiceList.toMutableList()

        proposalServiceList.removeAll {
            it.id == event.proposalService.id || it.id == "drag"
        }

        val index =
            if (event.index > -1) {
                // Increase index of all proposal services with the same date and index >= event.index
                proposalServiceList
                    .sortedBy { it.index }
                    .groupBy { it.startDate }
                    .forEach { (_, proposalServiceModelList) ->
                        proposalServiceModelList
                            .forEachIndexed { index, proposalServiceModel ->
                                val i = proposalServiceList.indexOf(proposalServiceModel)

                                if (i == -1) {
                                    return@forEachIndexed
                                }

                                val sortIndex =
                                    if (index >= event.index && proposalServiceModel.startDate == event.date) {
                                        index + 1
                                    } else {
                                        index
                                    }

                                proposalServiceList[i] =
                                    proposalServiceModel.copy(
                                        index = sortIndex,
                                    )
                            }
                    }

                event.index
            } else {
                proposalServiceList
                    .filter { it.startDate == event.date }
                    .size
            }

        val proposalService =
            event.proposalService.copy(
                id =
                    if (event.dropped && event.proposalService.id == "drag") {
                        Uuid.random().toString()
                    } else {
                        event.proposalService.id
                    },
                index = index,
                startDate = event.date,
            )

        proposalServiceList.add(proposalService)

        state.update {
            it.copy(
                proposalServiceList = proposalServiceList,
            )
        }

        if (event.dropped && validation.addEditProposalForm.validate()) {
            if (state.value.proposal.value.id.isNotEmpty())
                editProposal(event.context)
            else
                addProposal(event.context)
        }
    }

    private fun deleteProposalService(event: AddEditProposalEvent.DeleteProposalService) {
        state.update {
            it.copy(
                proposalServiceList =
                    it.proposalServiceList.filter { proposalServiceModel ->
                        proposalServiceModel.startDate != event.date ||
                            proposalServiceModel.index != event.index
                    },
            )
        }
    }

    private fun markProposalServiceAsPaid(event: AddEditProposalEvent.MarkProposalServiceAsPaid) {
        state.update {
            it.copy(
                proposalServiceList =
                    it.proposalServiceList
                        .update(
                            predicate = { proposalServiceModel ->
                                proposalServiceModel.id == event.proposalService.id
                            },
                            update = { proposalServiceModel ->
                                proposalServiceModel.copy(
                                    paid = true,
                                )
                            },
                        ),
            )
        }
    }

    private fun hideServiceOnProposal(event: AddEditProposalEvent.HideServiceOnProposal) {
        state.update {
            it.copy(
                proposalServiceList =
                    it.proposalServiceList
                        .update(
                            predicate = { proposalServiceModel ->
                                proposalServiceModel.id == event.proposalService.id
                            },
                            update = { proposalServiceModel ->
                                proposalServiceModel.copy(
                                    hideOnProposal = true,
                                )
                            },
                        ),
            )
        }
    }
    private fun clearSuccessState() {
        state.value = state.value.copy(addEditProposal = State())
    }

    private fun updateProposalFields(event: AddEditProposalEvent.UpdateProposalFields) {
        state.value.proposal.value = event.proposal
    }

    private fun updateProposalTravelAgent(event: AddEditProposalEvent.UpdateProposalTravelAgent) =
        getData(
            getData = { travelAgentRepository.getById(event.travelAgentId) },
            getState = { state.value.updateTravelAgent },
            setState = { data -> state.update { it.copy(updateTravelAgent = data) } },
            onSuccess = { ta ->
                state.update {
                    it.copy(
                        updateTravelAgent = State(),
                    )
                }.also {
                    state.value.proposal.value = state.value.proposal.value.copy(
                        travelAgent = ta,
                        commission = ta.commission,
                    )
                }
            },
        )

    private fun selectProposalServiceDate(event: AddEditProposalEvent.SelectProposalServiceDate) {
        state.value =
            state.value.copy(
                selectedProposalServiceDate = event.date,
            )
        state.value.selectedProposalService.value = null
    }

    private fun addProposalServiceToGroup(event: AddEditProposalEvent.AddProposalServiceToGroup) {
        val proposalServiceList = state.value.proposalServiceList.toMutableList()
        val index = proposalServiceList.indexOfFirst { it.id == event.id }
        if (index == -1) return
        proposalServiceList[index] =
            proposalServiceList[index].copy(
                grouped = proposalServiceList[index].grouped?.let { grouped -> !grouped } ?: true,
            )
        state.update {
            it.copy(
                proposalServiceList = proposalServiceList,
            )
        }
    }

    private fun proposalServiceGrouped(event: AddEditProposalEvent.ProposalServiceGrouped) {
        state.value =
            state.value.copy(
                proposalServiceList =
                    state.value.proposalServiceList.map {
                        if (it.startDate == event.date) {
                            it.copy(
                                grouped =
                                    when {
                                        it.id in event.ids ->
                                            true

                                        event.isCtrlPressed ->
                                            it.grouped

                                        else ->
                                            false
                                    },
                            )
                        } else {
                            it
                        }
                    },
            )
    }

    private fun updateSelectedProposalService(event: AddEditProposalEvent.UpdateSelectedProposalService) {
        state.value.selectedProposalService.value = event.proposalService

        if (state.value.selectedProposalService.value == null) return

        val proposalServiceList = state.value.proposalServiceList.toMutableList()
        var index = proposalServiceList.indexOfFirst { it.id == state.value.selectedProposalService.value?.id }

        if (index == -1) {
            index =
                proposalServiceList.indexOfFirst {
                    it.startDate == state.value.selectedProposalService.value?.startDate &&
                        it.index == state.value.selectedProposalService.value?.index
                }

            if (index != -1) {
                proposalServiceList[index] =
                    proposalServiceList[index].copy(
                        additionalInfo = state.value.selectedProposalService.value?.additionalInfo,
                        price = state.value.selectedProposalService.value?.price,
                        supplier = state.value.selectedProposalService.value?.supplier,
                        editedDescription = state.value.selectedProposalService.value?.editedDescription,
                    )
            } else {
                return
            }
        } else {
            proposalServiceList[index] = state.value.selectedProposalService.value ?: ProposalServiceModel.empty
        }
        state.update {
            it.copy(
                proposalServiceList = proposalServiceList,
            )
        }
    }

    private fun unselectProposalService(event: AddEditProposalEvent.UnselectProposalService) {
        if (state.value.selectedProposalService.value?.id == event.proposalService?.id) {
            state.value.selectedProposalService.value = null
        }
    }

    private fun updateProposalService(event: AddEditProposalEvent.UpdateProposalService) {
        val proposalServiceList = state.value.proposalServiceList.toMutableList()

        val proposalServiceIndex =
            proposalServiceList.indexOfFirst { it.id == event.proposalService.id }

        if (proposalServiceIndex == -1) return

        proposalServiceList[proposalServiceIndex] = event.proposalService

        state.value.selectedProposalService.value = event.proposalService

        state.value =
            state.value.copy(
                proposalServiceList = proposalServiceList,
            )
    }

    private fun addClient(event: AddEditProposalEvent.AddClient) {
        state.update {
            it.copy(
                getAllClient =
                    it.getAllClient.copy(
                        data = it.getAllClient.data.orEmpty() + event.client,
                    ),
            )
        }

        val primaryClient =
            if (state.value.proposal.value.clients.orEmpty().isEmpty())
                event.client
            else
                state.value.proposal.value.primaryClient

        state.value.proposal.value =
            state.value.proposal.value.copy(
                clients = state.value.proposal.value.clients.orEmpty() + event.client.id,
                primaryClient = primaryClient,
            )
    }

    private fun editClient(event: AddEditProposalEvent.EditClient) {
        state.update {
            it.copy(
                getAllClient =
                    it.getAllClient.copy(
                        data =
                            it.getAllClient.data.orEmpty()
                                .update(
                                    predicate = { client -> client.id == event.client.id },
                                    update = { event.client },
                                ),
                    ),
            )
        }
    }

    private fun deleteClient(event: AddEditProposalEvent.DeleteClient) {
        state.update {
            it.copy(
                getAllClient = it.getAllClient.copy(
                    data =
                        it.getAllClient.data.orEmpty()
                            .filter { client ->
                                client.id != event.client.id
                            },
                ),
            )
        }

        val primaryClient =
            if (event.client.id == state.value.proposal.value.primaryClient?.id)
                state.value.getAllClient.data
                    .orEmpty()
                    .firstOrNull { it.id != event.client.id }
            else
                state.value.proposal.value.primaryClient

        state.value.proposal.value =
            state.value.proposal.value.copy(
                clients =
                    state.value.proposal.value.clients
                        .orEmpty()
                        .filter { id ->
                            id != event.client.id
                        },
                primaryClient = primaryClient,
            )
    }

    private fun updateAlertSaveProposalDialog(event: AddEditProposalEvent.UpdateAlertSaveProposalDialog) {
        state.value =
            state.value.copy(
                openAlertSaveProposalDialog = event.isVisible,
            )
    }

    private suspend fun insertFiles(context: PlatformContext): Boolean {
        return fileViewModel.insertFiles(
            folderName = "proposal_cover",
            bucketName = "images",
            entityType = "proposal",
            context = context,
            onSuccess = { files ->
                state.value.proposal.value = state.value.proposal.value.copy(
                    coverImage = files.first(),
                )
            },
            onFailure = {
                state.update {
                    it.copy(
                        addEditProposal = State.failure(),
                    )
                }
            },
        )
    }
}
